import { supabase } from '@/integrations/supabase/client';

export type DealStage = 'new' | 'contact' | 'scheduled' | 'proposal' | 'negotiation' | 'won' | 'lost';

export interface StageTransitionRequirements {
  requiresQualification?: boolean;  // urgency, objective
  requiresMeetingDate?: boolean;
  requiresPostMeeting?: boolean;    // meeting happened, eligibility, proposal value, recommended process
  requiresLostReason?: boolean;
  requiresProposalValue?: boolean;
}

export interface DealForTransition {
  id: string;
  stage: DealStage;
  contact_id?: string;
  // From deal_qualification table
  urgency?: string;
  objective?: string;
  // From deals table
  reuniao_data?: string;
  reuniao_aconteceu?: boolean;
  proposal_value?: number;
  recommended_process?: string;
  detailed_lost_reason?: string;
  detailed_lost_notes?: string;
  noshow_count?: number;
  // Computed
  hasEligibilityEvaluated?: boolean;
}

// Define what's required for each transition
export const STAGE_TRANSITION_RULES: Record<string, StageTransitionRequirements> = {
  'contact_to_scheduled': {
    requiresQualification: true,
    requiresMeetingDate: true,
  },
  'contact_to_lost': {
    requiresLostReason: true,
  },
  'scheduled_to_proposal': {
    requiresPostMeeting: true,
  },
  'scheduled_to_lost': {
    requiresLostReason: true,
  },
  'proposal_to_lost': {
    requiresLostReason: true,
  },
  'negotiation_to_won': {
    requiresProposalValue: true,
  },
  'negotiation_to_lost': {
    requiresLostReason: true,
  },
  'new_to_lost': {
    requiresLostReason: true,
  },
};

export function getTransitionKey(from: DealStage, to: DealStage): string {
  return `${from}_to_${to}`;
}

export function getTransitionRequirements(from: DealStage, to: DealStage): StageTransitionRequirements {
  const key = getTransitionKey(from, to);
  return STAGE_TRANSITION_RULES[key] || {};
}

export function validateTransition(
  deal: DealForTransition, 
  toStage: DealStage
): { valid: boolean; missingFields: string[] } {
  const requirements = getTransitionRequirements(deal.stage, toStage);
  const missingFields: string[] = [];

  if (requirements.requiresQualification) {
    if (!deal.urgency) missingFields.push('urgency');
    if (!deal.objective) missingFields.push('objective');
  }

  if (requirements.requiresMeetingDate) {
    if (!deal.reuniao_data) missingFields.push('reuniao_data');
  }

  if (requirements.requiresPostMeeting) {
    if (!deal.reuniao_aconteceu) missingFields.push('reuniao_aconteceu');
    if (!deal.hasEligibilityEvaluated) missingFields.push('eligibility');
    if (!deal.proposal_value) missingFields.push('proposal_value');
    if (!deal.recommended_process) missingFields.push('recommended_process');
  }

  if (requirements.requiresLostReason) {
    if (!deal.detailed_lost_reason) missingFields.push('detailed_lost_reason');
  }

  if (requirements.requiresProposalValue) {
    if (!deal.proposal_value) missingFields.push('proposal_value');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}

export async function fetchDealForTransition(dealId: string): Promise<DealForTransition | null> {
  try {
    // Fetch deal
    const { data: deal, error: dealError } = await supabase
      .from('deals')
      .select('*')
      .eq('id', dealId)
      .single();
    
    if (dealError || !deal) {
      console.error('Error fetching deal:', dealError);
      return null;
    }

    // Fetch qualification
    const { data: qualification } = await supabase
      .from('deal_qualification')
      .select('urgency, objective')
      .eq('deal_id', dealId)
      .maybeSingle();

    // Fetch scheduled meeting from calls table
    const { data: scheduledMeeting } = await supabase
      .from('calls')
      .select('scheduled_for')
      .eq('deal_id', dealId)
      .eq('channel', 'video')
      .order('scheduled_for', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Check if has eligibility evaluated
    const { count: eligibilityCount } = await supabase
      .from('deal_eligibility')
      .select('id', { count: 'exact', head: true })
      .eq('deal_id', dealId);

    // Cast deal to any to access dynamic properties that may exist
    const dealAny = deal as Record<string, unknown>;

    return {
      id: deal.id,
      stage: deal.stage as DealStage,
      contact_id: deal.contact_id ?? undefined,
      urgency: qualification?.urgency ?? undefined,
      objective: qualification?.objective ?? undefined,
      reuniao_data: scheduledMeeting?.scheduled_for ?? deal.stage_scheduled_at ?? undefined,
      reuniao_aconteceu: (dealAny.reuniao_aconteceu as boolean | undefined) ?? undefined,
      proposal_value: deal.proposal_value ?? undefined,
      recommended_process: deal.recommended_process ?? undefined,
      detailed_lost_reason: deal.detailed_lost_reason ?? undefined,
      detailed_lost_notes: deal.detailed_lost_notes ?? undefined,
      noshow_count: deal.noshow_count ?? undefined,
      hasEligibilityEvaluated: (eligibilityCount || 0) > 0,
    };
  } catch (error) {
    console.error('Error in fetchDealForTransition:', error);
    return null;
  }
}

export async function validateDealForScheduled(dealId: string): Promise<{ valid: boolean; missingFields: string[] }> {
  const dealData = await fetchDealForTransition(dealId);
  
  if (!dealData) {
    return { valid: false, missingFields: ['deal_not_found'] };
  }

  const missingFields: string[] = [];
  
  if (!dealData.urgency) missingFields.push('urgency');
  if (!dealData.objective) missingFields.push('objective');
  if (!dealData.reuniao_data) missingFields.push('reuniao_data');

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
